import type { MessageResponse } from '@type/http';
import { Api } from '../config/api';
import type { ChatHistory } from '../types/ChatWindow';
import { getRequest } from './httpService';

/**
 * The `lastFetchedTimestamp` option is used to optimize message fetching by retrieving only the messages
 * that were sent after the last fetch. This avoids fetching the entire message history repeatedly,
 * reducing server load and improving performance, especially for users with large chat histories.
 */
export type TMessagesOfUser = {
  messages: ChatHistory[];
  // * False when the request failed. Callers cannot tell that from an empty
  // * `messages` alone, and the two mean opposite things: a fetched empty
  // * history says the session is gone, a failed fetch says nothing at all.
  isFetched: boolean;
};

export const getMessagesOfUser = async (
  userId: string,
  lastTimestamp?: number,
): Promise<TMessagesOfUser> => {
  const params = lastTimestamp ? `?since=${lastTimestamp}` : '';

  const response = await getRequest<MessageResponse>(
    `${Api.REQUESTS.messages}/${userId}${params}`,
  );

  // * A failed request still resolves with a body - the API error envelope -
  // * which is truthy but carries no chatHistory. Returning that as `undefined`
  // * broke every caller reading `.length` on the result.
  const isFetched = Array.isArray(response?.chatHistory);

  return {
    messages: isFetched ? response.chatHistory : [],
    isFetched,
  };
};

// * Core answers `{ chatHistory: [] }` for a stored user id that no longer
// * resolves to a session, and session deletion removes the whole session
// * document - so an empty history from a successful first fetch means there is
// * nothing to continue and the widget is back in its initial state. Only the
// * first fetch counts: later ones pass `since` and are empty in normal use.
export const isSessionMissing = (
  result: TMessagesOfUser,
  isInitialFetch: boolean,
): boolean =>
  isInitialFetch && result.isFetched && result.messages.length === 0;

/**
 * Extracts timestamp from a ChatHistory item
 * @param message The message to extract the timestamp from
 * @returns timestamp in milliseconds
 */
export const getMessageTimestamp = (message: ChatHistory): number => {
  return new Date(message.time).getTime();
};
